/**
 * IterationCard Component
 *
 * Displays a single iteration with its status, prompt, report preview, and actions.
 * Supports expanding to show full report content.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  TaskIteration,
  IterationStatus,
  IterationAttachment,
} from '@/types/tasks';
import {
  ITERATION_STATUS_CONFIG,
  ARTIFACT_TYPE_CONFIG,
  formatFileSize,
} from '@/types/tasks';
import { getIterationAttachmentDownloadUrl } from '@/services/api';
import ProgressPanel from './ProgressPanel';
import FileProcessingProgress from './FileProcessingProgress';
import FeedbackDialog from './FeedbackDialog';

// Check if file type supports preview
const canPreview = (fileName: string): boolean => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const previewableExtensions = [
    // Code
    'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'h', 'cs',
    'php', 'rb', 'go', 'rs', 'swift', 'kt', 'sh', 'bash', 'sql',
    'css', 'scss', 'less', 'yaml', 'yml', 'toml', 'ini', 'conf', 'json',
    // Markdown
    'md', 'markdown',
    // Images
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico',
    // Documents
    'pdf', 'docx', 'doc', 'xlsx', 'xls', 'csv',
    // HTML
    'html', 'htm',
    // Text
    'txt', 'log',
  ];
  return previewableExtensions.includes(ext);
};

interface IterationCardProps {
  taskId: string;
  iteration: TaskIteration;
  onViewReport?: (iteration: TaskIteration) => void;
  onEdit?: (iteration: TaskIteration) => void;
  onSubmit?: (iteration: TaskIteration) => void;
  onComplete?: (iteration: TaskIteration) => void;
  onCancel?: (iteration: TaskIteration) => void;
  onDelete?: (iteration: TaskIteration) => void;
  onDownloadAttachment?: (attachment: IterationAttachment) => void;
  onRate?: (iteration: TaskIteration, stars: number, feedback?: string) => void;
  onRequestRevision?: () => void;
  isLoading?: boolean;
}

export default function IterationCard({
  taskId,
  iteration,
  onViewReport,
  onEdit,
  onSubmit,
  onComplete,
  onCancel,
  onDelete,
  onDownloadAttachment,
  onRate,
  onRequestRevision,
  isLoading = false,
}: IterationCardProps) {
  const { t } = useTranslation('tasks');
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [selectedStars, setSelectedStars] = useState(0);

  // Preview attachment
  const handlePreviewAttachment = async (attachment: IterationAttachment) => {
    try {
      const { url } = await getIterationAttachmentDownloadUrl(
        taskId,
        iteration.id,
        attachment.id
      );
      navigate(`/tasks/${taskId}/iterations/${iteration.id}/attachments/${attachment.id}/preview`, {
        state: {
          fileName: attachment.file_name,
          fileSize: attachment.file_size,
          downloadUrl: url,
        },
      });
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to preview file');
    }
  };

  const statusConfig = ITERATION_STATUS_CONFIG[iteration.status as IterationStatus];

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const inputAttachments = iteration.attachments?.filter(a => a.artifact_type === 'input') || [];
  const outputAttachments = iteration.attachments?.filter(a => a.artifact_type === 'output') || [];

  const isInitialRequest = iteration.iteration_number === 1;
  const canEdit = iteration.status === 'draft';
  const canSubmit = iteration.status === 'draft';
  const canComplete = iteration.status === 'draft' || iteration.status === 'pending';
  // Can't cancel or delete iteration #1 (it's the task itself)
  const canCancel = !isInitialRequest && (iteration.status === 'draft' || iteration.status === 'pending');
  const canDelete = !isInitialRequest && iteration.status === 'draft';
  const hasReport = iteration.report && iteration.report.length > 0;

  return (
    <div
      className={`border rounded-lg overflow-hidden ${statusConfig.borderColor} ${statusConfig.bgColor}`}
    >
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between bg-white/50">
        <div className="flex items-center gap-3">
          <span className="text-lg">{statusConfig.icon}</span>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">
                {/* Iteration #1 = Initial Request (task itself), subsequent = Revision */}
                {iteration.iteration_number === 1 ? t('iteration.card.initialRequest') : t('iteration.card.revision', { number: iteration.iteration_number - 1 })}
              </span>
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.bgColor} ${statusConfig.color}`}
              >
                {t(`iterationStatus.${iteration.status}`)}
              </span>
              {iteration.created_by === 'ai' && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                  {t('iteration.card.aiCreated')}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {iteration.iteration_number === 1 ? t('iteration.card.taskCreated') : t('iteration.card.requested')} {formatDate(iteration.created_at)}
              {iteration.completed_at && (
                <span> | {t('iteration.card.completed')} {formatDate(iteration.completed_at)}</span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {hasReport && (
            <button
              onClick={() => onViewReport?.(iteration)}
              className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            >
              {t('iteration.card.viewReport')}
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          >
            <svg
              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* File processing progress for pending/dispatching iterations */}
      {(iteration.status === 'pending' || iteration.status === 'dispatching') && (
        <FileProcessingProgress
          taskId={taskId}
          iterationStatus={iteration.status as 'pending' | 'dispatching'}
          className="mx-4 my-3"
          compact={true}
        />
      )}

      {/* Progress Panel for processing iterations */}
      {iteration.status === 'processing' && (
        <ProgressPanel
          taskId={taskId}
          iterationId={iteration.id}
          compact={true}
          className="mx-4 my-3"
        />
      )}

      {/* Star Rating for completed/failed iterations */}
      {(iteration.status === 'completed' || iteration.status === 'failed') && (
        <div className="px-4 py-2 flex items-center gap-2 border-t border-gray-100 bg-white/30">
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => {
              const isRated = iteration.rating_stars != null;
              const filled = isRated
                ? star <= (iteration.rating_stars ?? 0)
                : star <= (hoveredStar || 0);
              return (
                <button
                  key={star}
                  type="button"
                  disabled={isRated || isLoading}
                  onClick={() => {
                    if (isRated) return;
                    if (star >= 4) {
                      // 4-5 stars: save immediately, no dialog
                      onRate?.(iteration, star);
                    } else {
                      // < 4 stars: open feedback dialog
                      setSelectedStars(star);
                      setShowFeedback(true);
                    }
                  }}
                  onMouseEnter={() => !isRated && setHoveredStar(star)}
                  onMouseLeave={() => !isRated && setHoveredStar(0)}
                  className={`p-0.5 transition-colors ${isRated ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
                  title={isRated ? undefined : `${star} star${star > 1 ? 's' : ''}`}
                >
                  <svg
                    className={`w-5 h-5 transition-colors ${filled ? 'text-yellow-400' : 'text-gray-300'}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </button>
              );
            })}
          </div>
          <span className="text-xs text-gray-500">
            {iteration.rating_stars != null
              ? t('iteration.card.rated')
              : t('iteration.card.rateResult')}
          </span>
        </div>
      )}

      {/* Feedback Dialog for low ratings */}
      <FeedbackDialog
        isOpen={showFeedback}
        stars={selectedStars}
        onSubmit={(feedback, requestRevision) => {
          setShowFeedback(false);
          onRate?.(iteration, selectedStars, feedback);
          if (requestRevision) {
            onRequestRevision?.();
          }
        }}
        onClose={() => setShowFeedback(false)}
      />

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 py-3 space-y-4 bg-white/30 border-t border-gray-100">
          {/* User Prompt */}
          {iteration.user_prompt && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-1">{t('iteration.card.request')}</h4>
              <p className="text-sm text-gray-600 bg-white/50 rounded-md p-3 whitespace-pre-wrap">
                {iteration.user_prompt}
              </p>
            </div>
          )}

          {/* Report Preview */}
          {hasReport && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                {t('iteration.card.report')}
                {iteration.report_created_by && (
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    iteration.report_created_by === 'ai'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {t('iteration.card.reportBy', { creator: iteration.report_created_by === 'ai' ? t('iteration.card.reportByAI') : t('iteration.card.reportByUser') })}
                  </span>
                )}
              </h4>
              <div className="text-sm text-gray-600 bg-white/50 rounded-md p-3 max-h-40 overflow-hidden relative">
                <p className="whitespace-pre-wrap line-clamp-4">
                  {iteration.report}
                </p>
                {iteration.report && iteration.report.length > 300 && (
                  <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white/90 to-transparent" />
                )}
              </div>
              <button
                onClick={() => onViewReport?.(iteration)}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700"
              >
                {t('iteration.card.readFullReport')}
              </button>
            </div>
          )}

          {/* Input Files */}
          {inputAttachments.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <span>{ARTIFACT_TYPE_CONFIG.input.icon}</span>
                {t('iteration.card.inputFiles', { count: inputAttachments.length })}
              </h4>
              <div className="space-y-1">
                {inputAttachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center justify-between p-2 bg-white/50 rounded-md"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-gray-400">📎</span>
                      <span className="text-sm text-gray-700 truncate">{attachment.file_name}</span>
                      <span className="text-xs text-gray-500">{formatFileSize(attachment.file_size)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {canPreview(attachment.file_name) && (
                        <button
                          onClick={() => handlePreviewAttachment(attachment)}
                          className="p-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded"
                          title={t('attachments.preview')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => onDownloadAttachment?.(attachment)}
                        className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded"
                        title={t('attachments.download')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Output Files */}
          {outputAttachments.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <span>{ARTIFACT_TYPE_CONFIG.output.icon}</span>
                {t('iteration.card.outputFiles', { count: outputAttachments.length })}
              </h4>
              <div className="space-y-1">
                {outputAttachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center justify-between p-2 bg-white/50 rounded-md"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-gray-400">📎</span>
                      <span className="text-sm text-gray-700 truncate">{attachment.file_name}</span>
                      <span className="text-xs text-gray-500">{formatFileSize(attachment.file_size)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {canPreview(attachment.file_name) && (
                        <button
                          onClick={() => handlePreviewAttachment(attachment)}
                          className="p-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded"
                          title={t('attachments.preview')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => onDownloadAttachment?.(attachment)}
                        className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                        title={t('attachments.download')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
            {canEdit && (
              <button
                onClick={() => onEdit?.(iteration)}
                disabled={isLoading}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md transition-colors disabled:opacity-50"
              >
                {t('iteration.card.edit')}
              </button>
            )}
            {canSubmit && (
              <button
                onClick={() => onSubmit?.(iteration)}
                disabled={isLoading}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
              >
                {t('iteration.card.submitForProcessing')}
              </button>
            )}
            {canComplete && (
              <button
                onClick={() => onComplete?.(iteration)}
                disabled={isLoading}
                className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-md transition-colors disabled:opacity-50"
              >
                {t('iteration.card.completeManually')}
              </button>
            )}
            {canCancel && (
              <button
                onClick={() => onCancel?.(iteration)}
                disabled={isLoading}
                className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-md transition-colors disabled:opacity-50"
              >
                {t('iteration.card.cancel')}
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => onDelete?.(iteration)}
                disabled={isLoading}
                className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
              >
                {t('iteration.card.delete')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
